import _orderBy from 'lodash.orderby'
import {
  formatCurrency,
  formatTime,
  LOW_BALANCE_WARNING,
  maskPhoneNumber,
  getPhoneNumberCountryCodes
} from '@smarttransit/common'

const THEME = {
  success: '#4CAF50',
  warning: '#FFC107',
  error: '#f65122'
}

const EXPIRED_TICKET_MANIFEST_GRACE_MSECS = 5 * 60 * 1000

function getTicketsForTrip (passengerTrip) {
  const logTrips = Array.isArray(passengerTrip && passengerTrip.passengerTransactionLogTrips)
    ? passengerTrip.passengerTransactionLogTrips
    : []
  return logTrips.map((o) => (o && o.passengerTransactionLog)).filter((o) => (o && o.id))
}

function getLatestTicket (tickets) {
  return [].concat(tickets || []).sort((a, b) => (
    new Date(b.dateCreated).getTime() - new Date(a.dateCreated).getTime()
  ))[0]
}

function getValidTicketForTrip (passengerTrip) {
  const tickets = getTicketsForTrip(passengerTrip)
  return tickets.find((o) => (o.logStatus !== 'expired')) || getLatestTicket(tickets) || null
}

function getTimestamp (value) {
  const timestamp = value ? new Date(value).getTime() : NaN
  return Number.isNaN(timestamp) ? null : timestamp
}

function isTimestampExpired (timestamp, now) {
  if (timestamp === null) {
    return false
  }

  return timestamp <= now
}

function getTicketStatusExpiredTimestamp (ticket, metadataExpiresTimestamp) {
  if (ticket.logStatus !== 'expired') {
    return null
  }

  if (metadataExpiresTimestamp !== null) {
    return metadataExpiresTimestamp
  }

  const updatedTimestamp = getTimestamp(ticket.dateUpdated)
  if (updatedTimestamp !== null) {
    return updatedTimestamp
  }

  return getTimestamp(ticket.dateCreated)
}

function resolveExpiredTicketTimestamp (ticket, now = Date.now()) {
  if (!ticket) {
    return null
  }

  const metadataExpires = ticket.logMetadata && ticket.logMetadata.expires
    ? ticket.logMetadata.expires
    : null
  const metadataExpiresTimestamp = getTimestamp(metadataExpires)
  if (isTimestampExpired(metadataExpiresTimestamp, now)) {
    return metadataExpiresTimestamp
  }

  return getTicketStatusExpiredTimestamp(ticket, metadataExpiresTimestamp)
}

function isExpiredTicketBeyondManifestGrace (ticket, now = Date.now()) {
  const expiredTimestamp = resolveExpiredTicketTimestamp(ticket, now)
  if (expiredTimestamp === null) {
    return false
  }

  return expiredTimestamp + EXPIRED_TICKET_MANIFEST_GRACE_MSECS <= now
}

function isPrepaidTripStillValid (passengerTrip, { now = Date.now() } = {}) {
  if (!passengerTrip.datePrepaid) {
    return false
  }

  const ticket = getValidTicketForTrip(passengerTrip)
  return !ticket || !isExpiredTicketBeyondManifestGrace(ticket, now)
}

function isCompletedNonPrepaidTrip (passengerTrip) {
  return passengerTrip.completed && !passengerTrip.datePrepaid
}

function isTripCandidateForVehicle (passengerTrip, transportationProfileId) {
  return passengerTrip.transportationProfileId === transportationProfileId &&
    !isCompletedNonPrepaidTrip(passengerTrip)
}

function hasLiveManifestAdmission (passengerTrip, boardedPassengerProfileIds, now) {
  if (boardedPassengerProfileIds.indexOf(passengerTrip.passengerProfileId) !== -1) {
    return true
  }

  return isPrepaidTripStillValid(passengerTrip, { now })
}

function isTripAboardVehicle (passengerTrip, { transportationProfileId, boardedPassengerProfileIds, now = Date.now() }) {
  if (!isTripCandidateForVehicle(passengerTrip, transportationProfileId)) {
    return false
  }

  const ticket = getValidTicketForTrip(passengerTrip)
  if (isExpiredTicketBeyondManifestGrace(ticket, now)) {
    return false
  }

  return hasLiveManifestAdmission(passengerTrip, boardedPassengerProfileIds, now)
}

function isPassengerPaid (passengerTrip) {
  return Boolean(
    passengerTrip.datePrepaid ||
    passengerTrip.datePaid ||
    (
      passengerTrip.metadata &&
      passengerTrip.metadata.isProgressive &&
      !passengerTrip.metadata.lastError
    )
  )
}

function getBoardedPassengerProfileIds (passengerHash) {
  if (!Array.isArray(passengerHash) || !passengerHash.length) {
    return []
  }

  return [...new Set(passengerHash.map((o) => o.profileid).filter(Boolean))]
}

function onlyTripsAboardVehicle (passengerTrips, { transportationProfileId, boardedPassengerProfileIds, now = Date.now() }) {
  if (!Array.isArray(passengerTrips)) {
    return []
  }

  const boardedIds = Array.isArray(boardedPassengerProfileIds) ? boardedPassengerProfileIds : []

  return passengerTrips.filter((passengerTrip) => isTripAboardVehicle(passengerTrip, {
    transportationProfileId,
    boardedPassengerProfileIds: boardedIds,
    now
  }))
}

function formatLivePassengerRow (passengerTrip, boardedPassenger) {
  const passengerProfile = passengerTrip.passengerProfile || {}
  const identifiers = Array.isArray(passengerProfile.passengerIdentifiers)
    ? passengerProfile.passengerIdentifiers
    : []
  const passengerIdentifier = identifiers[0] || {}
  const ticket = getValidTicketForTrip(passengerTrip)
  const phoneNumber = passengerIdentifier.phoneNumber || null
  const userPayStatus = isPassengerPaid(passengerTrip)
  const balance = passengerProfile.balance
  const extremelyLowBalance = passengerTrip.metadata && passengerTrip.metadata.lastErrorCode === 'insufficient-funds'
  const lowBalance = !userPayStatus && typeof balance === 'number' && balance <= LOW_BALANCE_WARNING

  let amount = passengerTrip.cost
  let prepaidGuest = false
  let expiresTimestamp = null
  let previousReconfirmCode = null

  if (ticket) {
    amount = ticket.amount * -1
    prepaidGuest = !!(ticket.logMetadata && ticket.logMetadata.prepaidGuest)
    previousReconfirmCode = ticket.logMetadata && ticket.logMetadata.reconfirmCode
      ? ticket.logMetadata.reconfirmCode
      : null

    const rawExpires = ticket.logMetadata && ticket.logMetadata.expires
    if (rawExpires && Date.now() > new Date(rawExpires).getTime()) {
      expiresTimestamp = rawExpires
    }
  }

  const reconfirmCode = boardedPassenger && boardedPassenger.reconfirmcode
    ? String(boardedPassenger.reconfirmcode)
    : null

  return {
    passengerProfileId: passengerTrip.passengerProfileId,
    passengerTripId: passengerTrip.id,
    time: formatTime(passengerTrip.dateUpdated || passengerTrip.dateCreated),
    dateUpdatedTimestamp: new Date(passengerTrip.dateUpdated || passengerTrip.dateCreated).getTime(),
    phoneNumber,
    phoneNumberLabel: phoneNumber
      ? maskPhoneNumber(phoneNumber, getPhoneNumberCountryCodes())
      : (passengerTrip.passengerProfileId || 'Unknown'),
    amount,
    amountLabel: formatCurrency(amount),
    userPayStatus,
    lowBalance,
    extremelyLowBalance,
    passengerTripError: passengerTrip.metadata && passengerTrip.metadata.lastError
      ? passengerTrip.metadata.lastError
      : null,
    balanceLabel: formatCurrency(balance),
    balanceLowThemeColor: userPayStatus
      ? THEME.success
      : (extremelyLowBalance ? THEME.error : THEME.warning),
    prepaid: Boolean(passengerTrip.datePrepaid),
    prepaidGuest,
    reconfirmCode: reconfirmCode || (previousReconfirmCode ? String(previousReconfirmCode) : null),
    expiresTimestamp
  }
}

/**
 * Build display rows for the admin live passengers dialog from Redis passenger hash + trip records.
 */
function buildLivePassengerDisplayRows ({ passengerHash, passengerTrips, transportationProfileId }) {
  const boardedPassengerProfileIds = getBoardedPassengerProfileIds(passengerHash)
  const boardedByProfileId = {}

  ;(Array.isArray(passengerHash) ? passengerHash : []).forEach((passenger) => {
    if (passenger && passenger.profileid) {
      boardedByProfileId[passenger.profileid] = passenger
    }
  })

  const tripsAboard = onlyTripsAboardVehicle(passengerTrips, {
    transportationProfileId,
    boardedPassengerProfileIds
  })

  let rows = tripsAboard.map((passengerTrip) => {
    return formatLivePassengerRow(passengerTrip, boardedByProfileId[passengerTrip.passengerProfileId])
  })

  const tripProfileIds = new Set(rows.map((o) => o.passengerProfileId).filter(Boolean))
  boardedPassengerProfileIds.forEach((profileId) => {
    if (tripProfileIds.has(profileId)) {
      return
    }

    const boardedPassenger = boardedByProfileId[profileId] || {}
    const timestamp = boardedPassenger.timestamp
      ? new Date(boardedPassenger.timestamp).getTime()
      : Date.now()

    rows.push({
      passengerProfileId: profileId,
      passengerTripId: boardedPassenger.tripid || null,
      time: formatTime(boardedPassenger.timestamp || new Date()),
      dateUpdatedTimestamp: timestamp,
      phoneNumber: null,
      phoneNumberLabel: profileId,
      amount: null,
      amountLabel: '—',
      userPayStatus: false,
      lowBalance: false,
      extremelyLowBalance: false,
      passengerTripError: null,
      balanceLabel: '—',
      balanceLowThemeColor: THEME.warning,
      prepaid: false,
      prepaidGuest: false,
      reconfirmCode: boardedPassenger.reconfirmcode ? String(boardedPassenger.reconfirmcode) : null,
      expiresTimestamp: null
    })
  })

  if (rows.length > 1) {
    rows = _orderBy(rows, ['dateUpdatedTimestamp'], ['desc'])
    const extremelyLowBalancePassengers = rows.filter((o) => o.extremelyLowBalance)
    if (extremelyLowBalancePassengers.length && extremelyLowBalancePassengers.length !== rows.length) {
      rows = extremelyLowBalancePassengers.concat(rows.filter((o) => !o.extremelyLowBalance))
    }
  }

  return rows
}

export {
  EXPIRED_TICKET_MANIFEST_GRACE_MSECS,
  THEME,
  isExpiredTicketBeyondManifestGrace,
  isPrepaidTripStillValid,
  isTripAboardVehicle,
  getBoardedPassengerProfileIds,
  buildLivePassengerDisplayRows
}
